/*
 * The drive's slice and its reducer: one pure function from (model, event) to a new model and a
 * verdict, over the module's own `drive.*` events. Core's `applyEvent` has already gated a durable event
 * by `eventId` before a case here runs.
 *
 * The slice is what more than one page shows at once and what an event keeps right: the spaces the
 * reader reaches (the sidebar's drives, the home's cards, every breadcrumb's first step) and the items
 * the pages of this visit have read or heard of, by id. A page keeps its own order — a folder's name
 * order, Recent's newest first — as a list of ids, and draws each row from here, so a rename, a star or
 * a trash heard on the socket shows on every page at once without a second read.
 *
 * The order rule of an item is its `changedAt`: of two views of one item the later change wins, so a
 * listing answered before an event of a newer write cannot undo it. A space carries no such number and
 * the newest view wins. Spaces are written to disk with the model, so the sidebar draws on the first
 * paint; items are this visit's (`perVisit`), because a cache of a busy drive's rows would be most of
 * the disk write and every one of them is read again when its page opens.
 *
 * An event of this module with nothing to change is still **applied**: the verdict is what moves the
 * catch-up cursor. "Unknown" is kept for a type from a newer server.
 */

import type { EventReducer } from "../../core/modules/registry.ts"
import type { Model } from "../../core/store/model.ts"
import { defineModuleSlice } from "../../core/store/moduleSlice.ts"
import { isDriveEventType, type DriveEvent } from "./events.ts"
import type { ItemView, SpaceList, SpaceView } from "./wire/drive.gen.ts"

export interface DriveSlice {
	/** The spaces the reader reaches, by id: My drive, Team and the shared drives. */
	spaces: Record<number, SpaceView>
	/** Whether `spaces` holds the server's whole answer at least once, rather than only what events brought. */
	spacesRead: boolean
	/** Whether Team exists yet; the first visit asks for it when it does not. */
	teamMade: boolean
	/** The items this visit has read or heard of, by id. */
	items: Record<number, ItemView>
}

export function emptyDriveSlice(): DriveSlice {
	return { spaces: {}, spacesRead: false, teamMade: false, items: {} }
}

const DRIVE_SLICE = defineModuleSlice("drive", emptyDriveSlice, { perVisit: ["items"] })

/** This module's slice; one shared empty slice until something was read. */
export const driveSlice = DRIVE_SLICE.of

/** The model with this module's slice replaced, keeping the identity of everything else. */
export const withDriveSlice = DRIVE_SLICE.with

/** The slice as a new visit starts from it: the spaces, never this visit's items. */
export const hydrateDriveSlice = DRIVE_SLICE.hydrate

/** What of the slice is written to disk: everything but the items (see the file's comment). */
export function dehydrateDriveSlice(slice: DriveSlice): Omit<DriveSlice, "items"> {
	return { spaces: slice.spaces, spacesRead: slice.spacesRead, teamMade: slice.teamMade }
}

/** The slice holding the server's whole list of spaces: a space it no longer names is gone. */
export function withSpaceList(slice: DriveSlice, list: SpaceList): DriveSlice {
	const spaces: Record<number, SpaceView> = {}
	for (const space of list.spaces) spaces[space.id] = space
	return { ...slice, spaces, spacesRead: true, teamMade: list.teamMade }
}

/**
 * The slice with one space's newest view. `mine` is the reader's own fact, which an event — one for the
 * whole scope — cannot carry: the view the reader's own list brought keeps it.
 */
export function withSpace(slice: DriveSlice, space: SpaceView): DriveSlice {
	const mine = slice.spaces[space.id]?.mine ?? space.mine
	return {
		...slice,
		spaces: { ...slice.spaces, [space.id]: { ...space, mine } },
		teamMade: slice.teamMade || space.builtin === "team",
	}
}

/** The slice without a space the reader no longer reaches, and without every item of it. */
export function withoutSpace(slice: DriveSlice, spaceId: number): DriveSlice {
	if (
		slice.spaces[spaceId] === undefined &&
		!Object.values(slice.items).some((i) => i.spaceId === spaceId)
	) {
		return slice
	}
	const spaces = Object.fromEntries(
		Object.entries(slice.spaces).filter(([, space]) => space.id !== spaceId),
	)
	const items = Object.fromEntries(
		Object.entries(slice.items).filter(([, item]) => item.spaceId !== spaceId),
	)
	return { ...slice, spaces, items }
}

/**
 * The slice with items' views, each kept only where it is not older than the one held. A view that
 * says nothing of a document's link and of an item's setting for AI chats — `drive.item.changed`
 * never does, since every reader fetches its own facts — keeps what the held view knew, so a rename
 * does not turn the band's globe back into a lock nor Details' setting back to nothing.
 */
export function withItems(slice: DriveSlice, views: readonly ItemView[]): DriveSlice {
	let items: Record<number, ItemView> | null = null
	for (const view of views) {
		const held = (items ?? slice.items)[view.id]
		if (held !== undefined && held.changedAt > view.changedAt) continue
		items ??= { ...slice.items }
		items[view.id] =
			held !== undefined && view.linkId === undefined && view.agentLevel === undefined
				? { ...view, ...sharingOf(held) }
				: view
	}
	return items === null ? slice : { ...slice, items }
}

/** An item's setting for AI chats as a view carries it: the effective level, its own, and whence. */
export type AgentFacts = Pick<ItemView, "agentLevel" | "agentOwn" | "agentFrom">

/** What a view says of an item's reach beyond its drive: a document's link and the AI chats' setting. */
function sharingOf(view: ItemView): Pick<ItemView, "linkId" | "linkLevel"> & AgentFacts {
	return {
		...(view.linkId === undefined ? {} : { linkId: view.linkId }),
		...(view.linkLevel === undefined ? {} : { linkLevel: view.linkLevel }),
		...(view.agentLevel === undefined ? {} : { agentLevel: view.agentLevel }),
		...(view.agentOwn === undefined ? {} : { agentOwn: view.agentOwn }),
		...(view.agentFrom === undefined ? {} : { agentFrom: view.agentFrom }),
	}
}

/** The slice with one item's link at a new level (`doc.link.changed`, which names no address). */
export function withLinkLevel(slice: DriveSlice, itemId: number, level: string): DriveSlice {
	const held = slice.items[itemId]
	if (held === undefined || held.linkLevel === level) return slice
	return { ...slice, items: { ...slice.items, [itemId]: { ...held, linkLevel: level } } }
}

/**
 * The slice with one item's setting for AI chats as *Who can open* last read or wrote it, where the
 * slice holds the item; the slice itself when nothing changes, so a reader that follows the item
 * reads it once.
 */
export function withAgentSetting(slice: DriveSlice, itemId: number, facts: AgentFacts): DriveSlice {
	const held = slice.items[itemId]
	if (held === undefined) return slice
	const next = { ...held, ...facts }
	const same =
		next.agentLevel === held.agentLevel &&
		next.agentOwn === held.agentOwn &&
		next.agentFrom === held.agentFrom
	return same ? slice : { ...slice, items: { ...slice.items, [itemId]: next } }
}

/** The slice without one item, which moved to another space. */
function withoutItem(slice: DriveSlice, itemId: number): DriveSlice {
	if (slice.items[itemId] === undefined) return slice
	const items = Object.fromEntries(
		Object.entries(slice.items).filter(([, item]) => item.id !== itemId),
	)
	return { ...slice, items }
}

/** The slice with an item's star, where the slice holds the item. */
function withStar(slice: DriveSlice, itemId: number, starred: boolean): DriveSlice {
	const held = slice.items[itemId]
	if (held === undefined || held.starred === starred) return slice
	return { ...slice, items: { ...slice.items, [itemId]: { ...held, starred } } }
}

/** The slice after one event of this module. Exhaustive: a new type is a compile error here. */
function next(slice: DriveSlice, event: DriveEvent): DriveSlice {
	switch (event.type) {
		case "drive.space.changed":
			return withSpace(slice, event.data)
		case "drive.space.left":
			return withoutSpace(slice, event.data.spaceId)
		case "drive.item.changed":
			return withItems(slice, [event.data])
		case "drive.item.left":
			return withoutItem(slice, event.data.itemId)
		case "drive.star.changed":
			return withStar(slice, event.data.itemId, event.data.starred)
		case "drive.batch.changed":
			// The store keeps no batch: the list of a drive's batches is read when it is shown, and
			// read again on this event by the page that shows it.
			return slice
	}
}

export const driveReducer: EventReducer = (model: Model, event) => {
	if (!isDriveEventType(event.type)) return { next: model, result: "unknown" }
	const slice = driveSlice(model)
	const after = next(slice, event as DriveEvent)
	return { next: after === slice ? model : withDriveSlice(model, after), result: "applied" }
}
